import { useEffect, useRef } from "react";
import { tv } from "tailwind-variants";

/**
 * Style
 */

const containerStyle = tv({
  base: 'w-full flex gap-6',
  variants: {
    padding: {
      none: '',
      normal: 'py-10',
      small: 'py-2',
    },
    paddingTop: {
      none: '',
      normal: 'pt-10',
      small: 'pt-2',
    },
    paddingBottom: {
      none: '',
      normal: 'pb-10',
      small: 'pb-2',
    },
    border: {
      true: 'border-b-[1px] border-border-light-mode dark:border-border-dark-mode',
    },
    arrangement: {
      grid: {
        container: "items-center lg:items-start lg:flex-row flex-col"
      },
      row: {
        container: "items-center lg:items-start lg:flex-row flex-col"
      },
      fullRow: {
        container: "items-center lg:items-start lg:flex-row flex-col"
      },
    }
  },
  defaultVariants: {
    border: false,
    padding: 'normal',
    paddingTop: 'none',
    paddingBottom: 'none',
    arrangement: 'grid',
  }
});

/**
 * FormContainer
 */

type Props = {
  children: JSX.Element
  title: string
  arrangement?: "grid" | "row" | "fullRow"
  border?: boolean
  padding?: "normal" | "small"
  gap?: "normal" | "small",
  boldTitle?: boolean,
} | {
  children: JSX.Element
  title: string
  arrangement?: "grid" | "row" | "fullRow"
  border?: boolean
  paddingTop?: "normal" | "small"
  paddingBottom?: "normal" | "small"
  gap?: "normal" | "small",
  boldTitle?: boolean,
}

function FormContainer({
  children,
  title,
  arrangement = "grid",
  border = true,
  gap = "normal",
  boldTitle = false,
  ...props
}: Props) {
  let padding = 'padding' in props ? props.padding : 'none';
  let paddingTop = 'paddingTop' in props ? props.paddingTop : 'none';
  let paddingBottom = 'paddingBottom' in props ? props.paddingBottom : 'none';

  if (padding === 'none' && paddingTop === 'none' && paddingBottom === 'none') {
    padding = 'normal';
  }

  const computedStyle = useRef(containerStyle({
    arrangement,
    padding: padding as any,
    paddingTop: paddingTop as any,
    paddingBottom: paddingBottom as any,
    border,
  }));

  useEffect(() => {
    computedStyle.current = containerStyle({
      arrangement,
      padding: padding as any,
      paddingTop: paddingTop as any,
      paddingBottom: paddingBottom as any,
      border,
    });
  }, [
    arrangement,
    border,
    padding,
    paddingTop,
    paddingBottom,
  ]);

  return (
    <div className={computedStyle.current}>
      <div className="w-28 flex justify-center lg:justify-start">
        <h1
          className='text-black dark:text-white'
          style={{
            fontWeight: boldTitle === true ? 'bold' : 'normal',
          }}>
          {title}
        </h1>
      </div>
      {
        arrangement === "grid" &&
        <div className={` flex-1 w-auto grid ${gap === 'normal' ? 'gap-6' : 'gap-3'} grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 grid-flow-row-dense`}>
          {
            children
          }
        </div>
      }
      {
        arrangement === "fullRow" &&
        <div className={` flex-1 w-auto flex flex-col ${gap === 'normal' ? 'gap-6' : 'gap-3'}`}>
          {
            children
          }
        </div>
      }
      {
        arrangement === "row" &&
        <div className={` flex-1 w-auto flex flex-col ${gap === 'normal' ? 'gap-6' : 'gap-3'} max-w-[550px]`}>
          {
            children
          }
        </div>
      }
    </div>
  );
}

export default FormContainer;